import jsPDF from "jspdf";

// Palette
const INK = [10, 15, 28];
const IVORY = [244, 239, 230];
const GOLD = [212, 175, 55];
const GOLD_DEEP = [150, 120, 40];
const EMERALD = [4, 120, 87];
const MUTED = [120, 110, 90];
const SUB = [90, 80, 60];

export function generateOverviewPdf() {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;

  const paintBg = () => {
    doc.setFillColor(...IVORY);
    doc.rect(0, 0, pageW, pageH, "F");
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(2);
    doc.line(margin, margin, margin + 36, margin);
  };
  const paintFooter = (pageNo, totalPages) => {
    const y = pageH - 40;
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.5);
    doc.line(margin, y - 14, pageW - margin, y - 14);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(
      "ADVIK VENTURES \u00B7 avadh@advikvc.com \u00B7 CAPABILITY CENTER AS A SERVICE",
      margin,
      y
    );
    doc.text(
      `Overview \u00B7 ${new Date().toLocaleDateString()} \u00B7 ${pageNo}/${totalPages}`,
      pageW - margin,
      y,
      { align: "right" }
    );
  };
  const eyebrow = (text, x, y, color = MUTED) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...color);
    doc.text(text, x, y);
  };
  const h1 = (text, x, y, size = 32) => {
    doc.setFont("times", "italic");
    doc.setFontSize(size);
    doc.setTextColor(...INK);
    const lines = doc.splitTextToSize(text, pageW - margin * 2 - (x - margin));
    doc.text(lines, x, y);
    return lines.length * (size * 0.9);
  };
  const h2 = (text, x, y, size = 20) => {
    doc.setFont("times", "italic");
    doc.setFontSize(size);
    doc.setTextColor(...INK);
    doc.text(text, x, y);
  };
  const body = (text, x, y, width, size = 10, color = SUB) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, width);
    doc.text(lines, x, y);
    return lines.length * (size + 3);
  };
  const label = (text, x, y, color = GOLD_DEEP) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...color);
    doc.text(text, x, y);
  };
  const hairline = (x, y, w = 24) => {
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(1);
    doc.line(x, y, x + w, y);
  };

  const TOTAL = 4;

  // ---------- Page 1 · Cover ----------
  paintBg();
  eyebrow("ADVIK VENTURES \u00B7 CAPABILITY CENTER AS A SERVICE", margin, margin + 22);
  eyebrow("OVERVIEW \u00B7 FOR PRINCIPALS", pageW - margin, margin + 22, MUTED);
  doc.text("OVERVIEW \u00B7 FOR PRINCIPALS", pageW - margin, margin + 22, { align: "right" });

  // Big title
  const titleY = 220;
  doc.setFont("times", "italic");
  doc.setFontSize(56);
  doc.setTextColor(...INK);
  doc.text("Build a specialized", margin, titleY);
  doc.setTextColor(...GOLD_DEEP);
  doc.text("capability center", margin, titleY + 56);
  doc.setTextColor(...INK);
  doc.text("in 90\u2014120 days.", margin, titleY + 112);

  hairline(margin, titleY + 150);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(...SUB);
  const sub = doc.splitTextToSize(
    "For family offices, private offices, and conglomerates rewriting the playbook from passive preservation to active value creation. We architect, staff, and operate specialized capability centers as an extension of your principal\u2019s office \u2014 at 40\u201460% lower cost than an in-house build.",
    pageW - margin * 2
  );
  doc.text(sub, margin, titleY + 180);

  // Cover metadata
  const metaY = pageH - 160;
  label("HUBS", margin, metaY);
  body("UAE  \u00B7  Singapore  \u00B7  India", margin, metaY + 16, 260, 11, INK);

  label("FRAMEWORKS", margin + 260, metaY);
  body("ADGM \u00B7 VARA \u00B7 MAS \u00B7 IFSCA \u00B7 MiCA \u00B7 GENIUS", margin + 260, metaY + 16, 260, 10, INK);

  label("DIRECT LINE", margin, metaY + 50);
  doc.setFont("times", "italic");
  doc.setFontSize(18);
  doc.setTextColor(...INK);
  doc.text("avadh@advikvc.com", margin, metaY + 72);

  label("ONLINE", margin + 260, metaY + 50);
  body("advikvc.com", margin + 260, metaY + 72, 260, 12, INK);

  paintFooter(1, TOTAL);

  // ---------- Page 2 · Manifesto + Framework ----------
  doc.addPage();
  paintBg();
  eyebrow("PAGE 2  \u00B7  MANIFESTO & FRAMEWORK", margin, margin + 22);
  eyebrow("READ 60 SEC", pageW - margin, margin + 22);
  doc.text("READ 60 SEC", pageW - margin, margin + 22, { align: "right" });

  let y = margin + 70;
  label("I \u00B7 MANIFESTO", margin, y);
  y += 18;
  h2("The mandate has changed. So must the machinery behind it.", margin, y, 22);
  y += 34;

  const chapters = [
    { n: "01", t: "Preservation is no longer a strategy.", d: "Inflation, geopolitical fragility, and generational turnover have rewritten the mandate. Preservation alone is a form of quiet erosion." },
    { n: "02", t: "Active value creation is the new mandate.", d: "Principals must compound capital across venture, private markets, operating businesses, and impact \u2014 with the same discipline that defined public-market wealth management." },
    { n: "03", t: "In-house teams cannot scale fast enough.", d: "Hiring cycles run 9\u201418 months. A full internal build can cost $5\u201415M annually before the first thesis is tested." },
    { n: "04", t: "A specialized capability center \u2014 as a service.", d: "Advik architects, staffs, and operates modular capability centers on your behalf. Working desk in 90\u2014120 days, at 40\u201460% lower cost." },
  ];
  chapters.forEach((c) => {
    doc.setFont("times", "italic");
    doc.setFontSize(20);
    doc.setTextColor(...GOLD_DEEP);
    doc.text(c.n, margin, y);
    doc.setFont("times", "italic");
    doc.setFontSize(13);
    doc.setTextColor(...INK);
    doc.text(c.t, margin + 40, y);
    y += 15;
    const h = body(c.d, margin + 40, y, pageW - margin * 2 - 40, 9, SUB);
    y += h + 10;
  });

  y += 6;
  hairline(margin, y);
  y += 20;
  label("II \u00B7 FRAMEWORK", margin, y);
  y += 18;
  h2("A four-phase build. Ninety days to a working desk.", margin, y, 20);
  y += 26;
  const phases = [
    { w: "Weeks 0\u20144", t: "Consult & Design", d: "Mandate, TOM blueprint, governance memo." },
    { w: "Weeks 4\u201410", t: "Build & Staff", d: "Talent, tooling stack, data room, OpSec." },
    { w: "Ongoing", t: "Execute & Operate", d: "Deals, IC-ready memos, portfolio operations." },
    { w: "Quarterly", t: "Measure & Optimize", d: "MOIC, IRR, DPI, impact reporting." },
  ];
  const phaseW = (pageW - margin * 2 - 12 * 3) / 4;
  phases.forEach((p, i) => {
    const x = margin + i * (phaseW + 12);
    doc.setDrawColor(210, 200, 180);
    doc.setLineWidth(0.5);
    doc.rect(x, y, phaseW, 90);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...EMERALD);
    doc.text(p.w.toUpperCase(), x + 10, y + 16);
    doc.setFont("times", "normal");
    doc.setFontSize(13);
    doc.setTextColor(...INK);
    doc.text(p.t, x + 10, y + 36);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...SUB);
    const lines = doc.splitTextToSize(p.d, phaseW - 20);
    doc.text(lines, x + 10, y + 54);
  });
  paintFooter(2, TOTAL);

  // ---------- Page 3 · Modules + Regions ----------
  doc.addPage();
  paintBg();
  eyebrow("PAGE 3  \u00B7  MODULES & REGIONAL FOOTPRINT", margin, margin + 22);
  doc.text("BUILD ONE OR STACK ALL", pageW - margin, margin + 22, { align: "right" });

  y = margin + 70;
  label("III \u00B7 MODULES", margin, y);
  y += 18;
  h2("Three modular desks. One or all.", margin, y, 20);
  y += 28;

  const modules = [
    { tag: "M01", title: "Strategic Finance Office", subs: "Venture Capital  \u00B7  Finance Office" },
    { tag: "M02", title: "Innovation Hub", subs: "Technology Diligence Center  \u00B7  Digital Asset Infrastructure" },
    { tag: "M03", title: "Sustainability & Impact Desk", subs: "ESG Framework  \u00B7  Renewable Energy & Electric Mobility" },
  ];
  modules.forEach((m) => {
    doc.setDrawColor(210, 200, 180);
    doc.setLineWidth(0.5);
    doc.line(margin, y - 4, pageW - margin, y - 4);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...GOLD_DEEP);
    doc.text(m.tag, margin, y + 12);
    doc.setFont("times", "italic");
    doc.setFontSize(16);
    doc.setTextColor(...INK);
    doc.text(m.title, margin + 50, y + 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...SUB);
    doc.text(m.subs, margin + 50, y + 28);
    y += 46;
  });

  y += 8;
  hairline(margin, y);
  y += 20;
  label("IV \u00B7 REGIONAL FOOTPRINT", margin, y);
  y += 18;
  h2("Three hubs. Six frameworks.", margin, y, 20);
  y += 28;

  // Hubs row
  const hubs = [
    { c: "AE", n: "United Arab Emirates", d: "Abu Dhabi \u00B7 Dubai" },
    { c: "SG", n: "Singapore", d: "Marina Bay \u00B7 VCC" },
    { c: "IN", n: "India", d: "Mumbai \u00B7 GIFT City" },
  ];
  const hubW = (pageW - margin * 2 - 12 * 2) / 3;
  hubs.forEach((rg, i) => {
    const x = margin + i * (hubW + 12);
    doc.setDrawColor(210, 200, 180);
    doc.setLineWidth(0.5);
    doc.rect(x, y, hubW, 68);
    doc.setFont("times", "italic");
    doc.setFontSize(22);
    doc.setTextColor(...GOLD_DEEP);
    doc.text(rg.c, x + 10, y + 26);
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...INK);
    doc.text(rg.n, x + 10, y + 46);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...SUB);
    doc.text(rg.d, x + 10, y + 60);
  });
  y += 82;

  // Frameworks table
  const frameworks = [
    ["ADGM \u00B7 FSRA", "UAE \u00B7 Abu Dhabi", "Fund & VA licensing"],
    ["Dubai \u00B7 VARA", "UAE \u00B7 Dubai", "Virtual assets"],
    ["MAS \u00B7 PSA", "Singapore", "Fund & DPT services"],
    ["GIFT City \u00B7 IFSCA", "India", "Offshore-in-onshore"],
    ["MiCA \u00B7 CASP", "European Union", "EU passport"],
    ["GENIUS \u00B7 FINTRAC", "Americas", "US + Canada"],
  ];
  const fwH = 22;
  frameworks.forEach((row, i) => {
    const yy = y + i * fwH;
    if (i % 2 === 0) {
      doc.setFillColor(232, 225, 209);
      doc.rect(margin, yy, pageW - margin * 2, fwH, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...INK);
    doc.text(row[0], margin + 8, yy + 15);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...SUB);
    doc.text(row[1], margin + 180, yy + 15);
    doc.text(row[2], margin + 320, yy + 15);
  });
  paintFooter(3, TOTAL);

  // ---------- Page 4 · Why + Outcomes + CTA ----------
  doc.addPage();
  paintBg();
  eyebrow("PAGE 4  \u00B7  WHY ADVIK, OUTCOMES & NEXT STEPS", margin, margin + 22);
  doc.text("REQUEST A WORKSHOP", pageW - margin, margin + 22, { align: "right" });

  y = margin + 70;
  label("V \u00B7 WHY ADVIK", margin, y);
  y += 18;
  h2("Faster, cheaper, defensible.", margin, y, 20);
  y += 28;

  const why = [
    ["90\u2014120 days", "to a working desk"],
    ["40\u201460%", "lower cost than in-house build"],
    ["50+ years", "of aggregate operator DNA"],
    ["5 sub-modules", "stacked to the mandate"],
    ["MOIC \u00B7 IRR \u00B7 Impact", "reported quarterly"],
  ];
  const wCol = (pageW - margin * 2 - 12 * 4) / 5;
  why.forEach((w, i) => {
    const x = margin + i * (wCol + 12);
    doc.setDrawColor(210, 200, 180);
    doc.setLineWidth(0.5);
    doc.rect(x, y, wCol, 90);
    doc.setFont("times", "italic");
    doc.setFontSize(15);
    doc.setTextColor(...GOLD_DEEP);
    const kLines = doc.splitTextToSize(w[0], wCol - 16);
    doc.text(kLines, x + 8, y + 24);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...SUB);
    const dLines = doc.splitTextToSize(w[1].toUpperCase(), wCol - 16);
    doc.text(dLines, x + 8, y + 68);
  });
  y += 108;

  hairline(margin, y);
  y += 18;
  label("VI \u00B7 OUTCOMES WE UNDERWRITE", margin, y);
  y += 16;

  const stats = [
    ["3.2x", "TARGET MOIC"],
    ["22%", "TARGET IRR"],
    ["5", "SDGs ANCHORED"],
    ["55%", "COST ADVANTAGE"],
  ];
  const sCol = (pageW - margin * 2 - 12 * 3) / 4;
  stats.forEach((s, i) => {
    const x = margin + i * (sCol + 12);
    doc.setDrawColor(210, 200, 180);
    doc.setLineWidth(0.5);
    doc.rect(x, y + 4, sCol, 72);
    doc.setFont("times", "italic");
    doc.setFontSize(24);
    doc.setTextColor(...GOLD_DEEP);
    doc.text(s[0], x + 10, y + 36);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...SUB);
    doc.text(s[1], x + 10, y + 60);
  });
  y += 100;

  // CTA panel
  hairline(margin, y);
  y += 20;
  doc.setFont("times", "italic");
  doc.setFontSize(22);
  doc.setTextColor(...INK);
  doc.text("Schedule a private strategy workshop.", margin, y);
  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SUB);
  const ctaLines = doc.splitTextToSize(
    "A confidential 90-minute session with our partners. We map your mandate, sketch the capability center design, and quantify the 90\u2014120 day path.",
    pageW - margin * 2
  );
  doc.text(ctaLines, margin, y);
  y += ctaLines.length * 13 + 14;

  // Contact block
  doc.setFillColor(...INK);
  doc.rect(margin, y, pageW - margin * 2, 62, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.text("DIRECT PARTNER LINE", margin + 16, y + 20);
  doc.setFont("times", "italic");
  doc.setFontSize(20);
  doc.setTextColor(...IVORY);
  doc.text("avadh@advikvc.com", margin + 16, y + 46);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.text("advikvc.com", pageW - margin - 16, y + 20, { align: "right" });
  doc.setFont("times", "italic");
  doc.setFontSize(14);
  doc.setTextColor(...IVORY);
  doc.text("By invitation \u00B7 confidential", pageW - margin - 16, y + 46, {
    align: "right",
  });

  paintFooter(4, TOTAL);

  doc.save("Advik-Ventures-Overview.pdf");
}
